import {
  CountUnit,
  FractionUnit,
  IngredientUnit,
  MeasuredIngredient,
  UnitType,
  VolumeUnit,
  WeightUnit,
} from "../models/ingredient";
import type { TFunc } from "../i18n/i18n";

// ─── Category lookups ─────────────────────────────────────────────────────────

export const UnitsByCategory: Record<UnitType, IngredientUnit[]> = {
  [UnitType.Weight]: Object.values(WeightUnit),
  [UnitType.Volume]: Object.values(VolumeUnit),
  [UnitType.Count]: Object.values(CountUnit),
  [UnitType.Other]: [],
};

export const FRACTION_SUPPORTED_UNITS = new Set([
  VolumeUnit.Cup,
  VolumeUnit.Tsp,
  VolumeUnit.Tbsp,
]);

export const supportsFractions = (unit: string | null | undefined): boolean => {
  return FRACTION_SUPPORTED_UNITS.has(unit as VolumeUnit);
};

// Display a scale multiplier without binary-float noise: a slider stepping by 0.1
// can commit values like 5.800000000000001, and stored sessions may already hold
// them. Round to 2dp and drop trailing zeros ("5.8", "1.5", "2").
export const formatMultiplier = (v: number): string => String(parseFloat(v.toFixed(2)));

// Units that read as whole words and take a plural form for amounts > 1
// ("2 cups"). Abbreviations (g, kg, ml, L, tsp, tbsp, oz, fl oz…) are never
// pluralised — "2 tsp", not "2 tsps". Extend this map to pluralise more units.
const PLURAL_UNITS: Record<string, string> = {
  [VolumeUnit.Cup]: "cups",
};

// Display form of a unit for a given numeric amount: the plural word when the
// amount is > 1 (e.g. "2 cups", "1 ½ cups"), else the stored singular ("1 cup",
// "½ cup"). A null/unknown amount stays singular. Only affects units in
// PLURAL_UNITS; everything else is returned unchanged.
export function pluraliseUnit(
  unit: string | null | undefined,
  amount: number | null,
): string {
  if (!unit) return "";
  if (amount !== null && amount > 1 && PLURAL_UNITS[unit]) return PLURAL_UNITS[unit];
  return unit;
}

// Stored unit token → base i18n key under "units.names". Each key holds a nested
// { one, other } so the label can localise *and* pluralise. Custom/free-text
// units (not in this map) fall through untranslated.
const UNIT_NAME_KEYS: Record<string, string> = {
  [WeightUnit.G]: "g",
  [WeightUnit.Kg]: "kg",
  [WeightUnit.Oz]: "oz",
  [WeightUnit.Lb]: "lb",
  [VolumeUnit.Ml]: "ml",
  [VolumeUnit.L]: "l",
  [VolumeUnit.Tsp]: "tsp",
  [VolumeUnit.Tbsp]: "tbsp",
  [VolumeUnit.Cup]: "cup",
  [VolumeUnit.FlOz]: "flOz",
  [CountUnit.Whole]: "whole",
  [CountUnit.Piece]: "piece",
  [CountUnit.Pinch]: "pinch",
  [CountUnit.Handful]: "handful",
  [CountUnit.Clove]: "clove",
  [CountUnit.Slice]: "slice",
  [CountUnit.Sprig]: "sprig",
};

// Display label for a unit at a given amount. With a `t` (from useT), the label
// is localised + pluralised via the "units.names.*" catalog — the display-only
// path. Without `t`, it falls back to the canonical English token (via
// pluraliseUnit), which is what editors, the bulk-edit parser, and serialisation
// must keep. Uses the ">1 → plural" cooking rule (½ cup, 1 ½ cups), not CLDR, so
// fractions read as singular. Custom units pass through unchanged.
export function unitLabel(
  unit: string | null | undefined,
  amount: number | null,
  t?: TFunc,
): string {
  if (!unit) return "";
  if (!t) return pluraliseUnit(unit, amount);
  const base = UNIT_NAME_KEYS[unit];
  if (!base) return unit;
  const plural = amount !== null && amount > 1;
  return t(`units.names.${base}.${plural ? "other" : "one"}`);
}

// The fractions the app supports, smallest→largest — for quick-pick UIs.
export const FRACTION_SYMBOLS: string[] = [
  FractionUnit.Eighth,
  FractionUnit.Quarter,
  FractionUnit.Third,
  FractionUnit.Half,
  FractionUnit.TwoThirds,
  FractionUnit.ThreeQuarters,
];

// Convert typed ASCII fractions ("1/2") to their unicode symbol ("½"), for the
// fractions the app supports. Only standalone tokens are converted (a lone
// "1/2", not the "1/2" inside "1/20"), so hand-typed quantities read nicely
// without changing anything the parser already accepts. Boundary is a captured
// leading char + a lookahead (no lookbehind — keeps it portable across JS
// engines, incl. Hermes).
const ASCII_TO_SYMBOL: Record<string, string> = {
  "1/2": FractionUnit.Half,
  "1/4": FractionUnit.Quarter,
  "3/4": FractionUnit.ThreeQuarters,
  "1/3": FractionUnit.Third,
  "2/3": FractionUnit.TwoThirds,
  "1/8": FractionUnit.Eighth,
};

export function prettifyFractions(text: string): string {
  return text.replace(
    /(^|[^\d/])(1\/2|1\/4|3\/4|1\/3|2\/3|1\/8)(?![\d/])/g,
    (_m, pre: string, frac: string) => pre + ASCII_TO_SYMBOL[frac],
  );
}

// Any remaining "a/b" fraction (one the app can't show as a symbol) → its
// decimal value, so a typed quantity never holds an unsupported fraction. Run
// after prettifyFractions — the supported ones are already symbols (no "/"
// left), so only unsupported fractions like "1/6" remain to convert.
function decimaliseUnsupportedFractions(text: string): string {
  return text.replace(
    /(^|[^\d/.])(\d+)\/(\d+)(?![\d/])/g,
    (m, pre: string, num: string, den: string) => {
      const d = Number(den);
      if (!d) return m;
      return pre + String(parseFloat((Number(num) / d).toFixed(3)));
    },
  );
}

// Normalise a hand-typed quantity's fractions to what the app supports: the six
// supported fractions become symbols (1/2 → ½); any other fraction becomes its
// decimal (1/6 → 0.167). Keeps the field free of unsupported fractions — which
// otherwise parse to nothing and lose the amount — mirroring how scaling shows
// out-of-set values as decimals.
export function normalizeQuantityFractions(text: string): string {
  return decimaliseUnsupportedFractions(prettifyFractions(text));
}

// ─── Scaling internals ────────────────────────────────────────────────────────

const WEIGHT_TO_G: Record<WeightUnit, number> = {
  [WeightUnit.G]: 1,
  [WeightUnit.Kg]: 1000,
  [WeightUnit.Oz]: 28.3495,
  [WeightUnit.Lb]: 453.592,
};

const VOLUME_TO_ML: Record<VolumeUnit, number> = {
  [VolumeUnit.Ml]: 1,
  [VolumeUnit.L]: 1000,
  [VolumeUnit.Tsp]: 4.92892,
  [VolumeUnit.Tbsp]: 14.7868,
  [VolumeUnit.Cup]: 236.588,
  [VolumeUnit.FlOz]: 29.5735,
};

const FRACTION_VALUES: {
  value: number;
  key: keyof typeof FractionUnit;
  symbol: string;
}[] = [
  { value: 3 / 4, key: "ThreeQuarters", symbol: FractionUnit.ThreeQuarters },
  { value: 2 / 3, key: "TwoThirds", symbol: FractionUnit.TwoThirds },
  { value: 1 / 2, key: "Half", symbol: FractionUnit.Half },
  { value: 1 / 3, key: "Third", symbol: FractionUnit.Third },
  { value: 1 / 4, key: "Quarter", symbol: FractionUnit.Quarter },
  { value: 1 / 8, key: "Eighth", symbol: FractionUnit.Eighth },
];

// A quantity string holding a numeric range like "1-2" (stored "lo-hi" by
// parseIngredientLine). Returns [lo, hi], or null when the quantity isn't a range.
const QUANTITY_RANGE_RE = /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/;
function parseQuantityRange(
  quantity: string | null | undefined,
): [number, number] | null {
  const m = (quantity ?? "").match(QUANTITY_RANGE_RE);
  if (!m) return null;
  const lo = Number(m[1]);
  const hi = Number(m[2]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  return [lo, hi];
}

/** Parse quantity + fraction fields into a single float. Returns null if unparseable. */
function parseAmount(ingredient: MeasuredIngredient): number | null {
  const qty = parseFloat(ingredient.quantity ?? "");
  const fracEntry = ingredient.fraction
    ? FRACTION_VALUES.find((f) => f.key === ingredient.fraction)
    : null;
  const fracValue = fracEntry?.value ?? 0;

  if (isNaN(qty) && !fracEntry) return null;
  return (isNaN(qty) ? 0 : qty) + fracValue;
}

/** Split a float into a whole number + nearest fraction symbol, or null if none fits closely. */
function toWholeAndFraction(value: number): { whole: number; fraction: string | null } {
  const whole = Math.floor(value);
  const remainder = value - whole;

  if (remainder < 0.05) return { whole, fraction: null };

  const nearest = FRACTION_VALUES.reduce((best, f) =>
    Math.abs(f.value - remainder) < Math.abs(best.value - remainder) ? f : best,
  );

  if (Math.abs(nearest.value - remainder) < 0.05) {
    return { whole, fraction: nearest.symbol };
  }

  return { whole: 0, fraction: null };
}

// Base metric units aren't measured by fractions — "⅓ ml" / "⅓ g" is nonsense
// (a metric conversion of ¼ cup would otherwise render "59 ⅛ ml"). These round
// to a plain number instead; everything else (cup/tbsp/tsp, L/kg, oz/lb) snaps
// to fractions.
const NON_FRACTIONABLE_UNITS = new Set<string>([VolumeUnit.Ml, WeightUnit.G]);

/** Format a scaled float as "whole fraction?" or a plain number. `fractionable`
 *  off (for ml/g) skips fraction snapping and rounds to a clean number. */
function formatAmount(scaled: number, fractionable = true): string {
  if (fractionable) {
    const { whole, fraction } = toWholeAndFraction(scaled);
    if (fraction !== null) {
      return [whole || null, fraction].filter(Boolean).join(" ");
    }
    return parseFloat(scaled.toFixed(2)).toString();
  }

  // ml / g — whole number for ≥ 10, one decimal below, never a fraction.
  return parseFloat(scaled.toFixed(scaled >= 10 ? 0 : 1)).toString();
}

// Display unit system: keep the recipe's own units, or auto-convert everything to
// metric / imperial. Weight and volume convert across systems; count/other don't.
export type UnitSystem = "original" | "metric" | "imperial";

/** Promote/demote to the most readable weight unit. `system` picks the target
 *  family; "original" keeps the ingredient's own metric/imperial family. */
function rescaleWeight(
  valueInG: number,
  originalUnit: WeightUnit,
  system: UnitSystem = "original",
): { value: number; unit: WeightUnit } {
  const isImperial =
    system === "imperial" ||
    (system === "original" &&
      (originalUnit === WeightUnit.Oz || originalUnit === WeightUnit.Lb));
  const candidates = isImperial
    ? [WeightUnit.Lb, WeightUnit.Oz]
    : [WeightUnit.Kg, WeightUnit.G];

  for (const unit of candidates) {
    const converted = valueInG / WEIGHT_TO_G[unit];
    if (converted >= 1) return { value: converted, unit };
  }

  const fallback = candidates[candidates.length - 1];
  return { value: valueInG / WEIGHT_TO_G[fallback], unit: fallback };
}

/** Promote/demote to the most readable volume unit. `system` picks the target
 *  family; "original" keeps the ingredient's own metric/imperial family. */
function rescaleVolume(
  valueInMl: number,
  originalUnit: VolumeUnit,
  system: UnitSystem = "original",
): { value: number; unit: VolumeUnit } {
  const isImperial =
    system === "imperial" ||
    (system === "original" &&
      (originalUnit === VolumeUnit.Cup ||
        originalUnit === VolumeUnit.FlOz ||
        originalUnit === VolumeUnit.Tsp ||
        originalUnit === VolumeUnit.Tbsp));
  // fl oz is intentionally not in the readable ladder: it's ~2 tbsp, so as the last
  // candidate it used to become the fallback for sub-1-tsp amounts (½ tsp → "⅛ fl
  // oz"). Cooking scales cup→tbsp→tsp; fl-oz originals still show as fl oz at scale 1
  // (that path doesn't rescale).
  const candidates = isImperial
    ? [VolumeUnit.Cup, VolumeUnit.Tbsp, VolumeUnit.Tsp]
    : [VolumeUnit.L, VolumeUnit.Ml];

  const options = candidates.map((unit) => ({
    unit,
    value: valueInMl / VOLUME_TO_ML[unit],
  }));

  // Keep a fractional CUP as cups when it lands on a standard measuring-cup
  // fraction (¼/⅓/½/⅔/¾) rather than demoting to an awkward tbsp count — "½ cup",
  // not "8 tbsp"; "⅔ cup", not "10 ⅔ tbsp". Cup-only and ≥ ¼, since tbsp/tsp read
  // better as whole/½ counts and ⅛ cup is conventionally "2 tbsp".
  if (isImperial) {
    const cupVal = valueInMl / VOLUME_TO_ML[VolumeUnit.Cup];
    const STD_FRACTIONS = [1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4];
    if (
      cupVal >= 0.25 &&
      cupVal < 1 &&
      STD_FRACTIONS.some((f) => Math.abs(f - cupVal) < 0.02)
    ) {
      return { value: cupVal, unit: VolumeUnit.Cup };
    }
  }

  // Default: the largest unit that reads as ≥ 1 (fallback: the smallest candidate).
  const def = options.find((o) => o.value >= 1) ?? options[options.length - 1];

  // Prefer a smaller unit when the default lands on an awkward fraction but the
  // smaller one is a clean whole number — so 4 tsp stays "4 tsp" instead of
  // promoting to "1 ⅓ tbsp", while 3 tsp still becomes "1 tbsp". Bounded so it
  // never trades a tidy larger unit for a silly count (1 ½ cups doesn't become
  // 24 tbsp); fl oz is never a step-down target (not a natural subdivision of
  // cup/tbsp/tsp). Applies equally to metric (1.5 L never becomes 1500 ml).
  // Tolerance well above the ~3e-5 drift in the tsp/tbsp/cup constants (they're
  // real ml values, not exact integer ratios) but below any real cooking amount.
  const isWhole = (v: number) => Math.abs(v - Math.round(v)) < 1e-3;
  if (!isWhole(def.value)) {
    const cleaner = options.find(
      (o) =>
        VOLUME_TO_ML[o.unit] < VOLUME_TO_ML[def.unit] &&
        o.value >= 1 &&
        o.value <= 12 &&
        isWhole(o.value),
    );
    if (cleaner) return { value: cleaner.value, unit: cleaner.unit };
  }

  return { value: def.value, unit: def.unit };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Convert a raw amount + unit into a target unit system, returning the numeric
 * value and the unit label to display. Weight (g/kg↔oz/lb) and volume
 * (ml/L↔cup/tbsp/tsp/fl oz) re-express across systems and stay readable via
 * rescale; count/other units and `system === "original"` pass through unchanged.
 * Used where a summed amount is formatted outside getQuantityString (the
 * shopping list) so it can honour the same units preference.
 */
export function convertMeasure(
  amount: number,
  unit: string | null | undefined,
  system: UnitSystem,
): { value: number; unit: string } {
  const u = unit ?? "";
  if (system === "original") return { value: amount, unit: u };
  if (u in WEIGHT_TO_G) {
    return rescaleWeight(amount * WEIGHT_TO_G[u as WeightUnit], u as WeightUnit, system);
  }
  if (u in VOLUME_TO_ML) {
    return rescaleVolume(amount * VOLUME_TO_ML[u as VolumeUnit], u as VolumeUnit, system);
  }
  return { value: amount, unit: u };
}

/**
 * Returns a formatted quantity string for an ingredient, optionally scaled and
 * converted to a target unit system.
 *
 * - `other` fields (e.g. "to taste") are returned as-is.
 * - Weight and volume units are promoted/demoted to stay readable (1000g → 1kg).
 *   With `unitSystem` "metric"/"imperial" they also convert across systems
 *   (g/kg ↔ oz/lb, ml/L ↔ cup/tbsp/tsp/fl oz); count/other units never convert.
 * - Scaled decimals snap to the nearest standard fraction where possible.
 * - `unitSystem` defaults to "original" so callers that must preserve the stored
 *   text (editors, serialization) get the recipe's own units untouched.
 * - `t` (from useT) localises the *unit label only* (numbers/fractions are
 *   locale-neutral). Pass it at display sites; omit it for editors/parsers/
 *   serialisation, which must keep the canonical English unit tokens.
 */
// Accepts the measured subset so it formats an IngredientSubstitute too.
export const getQuantityString = (
  ingredient: MeasuredIngredient,
  scale: number = 1,
  unitSystem: UnitSystem = "original",
  t?: TFunc,
): string => {
  if (ingredient.other) {
    return ingredient.other;
  }

  const unit = ingredient.unit as string;
  const isWeight = ingredient.unitType === UnitType.Weight && unit in WEIGHT_TO_G;
  const isVolume = ingredient.unitType === UnitType.Volume && unit in VOLUME_TO_ML;
  // Converting requires the numeric path even at scale 1 (to re-express the unit).
  const converting = unitSystem !== "original" && (isWeight || isVolume);

  // Ranges ("1-2 tbsp") scale each endpoint independently and re-join with a
  // hyphen ("2-4 tbsp"). Unit promotion/conversion is driven by the upper bound
  // so both ends share one readable unit. Handled here for every scale (incl. 1)
  // so a range never falls through to the single-value paths below.
  const range = parseQuantityRange(ingredient.quantity);
  if (range) {
    const lo = range[0] * scale;
    const hi = range[1] * scale;

    if (isWeight) {
      const g = WEIGHT_TO_G[unit as WeightUnit];
      const { unit: newUnit } = rescaleWeight(hi * g, unit as WeightUnit, unitSystem);
      const f = WEIGHT_TO_G[newUnit];
      const fr = !NON_FRACTIONABLE_UNITS.has(newUnit);
      return `${formatAmount((lo * g) / f, fr)}-${formatAmount((hi * g) / f, fr)} ${unitLabel(newUnit, (hi * g) / f, t)}`.trim();
    }

    if (isVolume) {
      const ml = VOLUME_TO_ML[unit as VolumeUnit];
      const { unit: newUnit } = rescaleVolume(hi * ml, unit as VolumeUnit, unitSystem);
      const f = VOLUME_TO_ML[newUnit];
      const hiVal = (hi * ml) / f;
      const fr = !NON_FRACTIONABLE_UNITS.has(newUnit);
      return `${formatAmount((lo * ml) / f, fr)}-${formatAmount(hiVal, fr)} ${unitLabel(newUnit, hiVal, t)}`.trim();
    }

    // Count / Other — scale both ends, keep the unit label unchanged.
    return [`${formatAmount(lo)}-${formatAmount(hi)}`, unitLabel(ingredient.unit, hi, t)]
      .filter(Boolean)
      .join(" ")
      .trim();
  }

  // scale === 1 with no conversion: use original values as-is to avoid float drift.
  if (scale === 1 && !converting) {
    const fraction = ingredient.fraction
      ? FractionUnit[ingredient.fraction as keyof typeof FractionUnit]
      : "";
    const quantityWithFraction = [ingredient.quantity, fraction]
      .filter(Boolean)
      .join(" ");
    const label = unitLabel(ingredient.unit, parseAmount(ingredient), t);
    return [quantityWithFraction, label].filter(Boolean).join(" ").trim();
  }

  const amount = parseAmount(ingredient);

  // Unparseable quantity — return original without scaling or converting.
  if (amount === null) {
    const fraction = ingredient.fraction
      ? FractionUnit[ingredient.fraction as keyof typeof FractionUnit]
      : "";
    const quantityWithFraction = [ingredient.quantity, fraction]
      .filter(Boolean)
      .join(" ");
    return [quantityWithFraction, unitLabel(ingredient.unit, null, t)]
      .filter(Boolean)
      .join(" ")
      .trim();
  }

  const scaled = amount * scale;

  if (isWeight) {
    const inG = scaled * WEIGHT_TO_G[unit as WeightUnit];
    const { value, unit: newUnit } = rescaleWeight(inG, unit as WeightUnit, unitSystem);
    return `${formatAmount(value, !NON_FRACTIONABLE_UNITS.has(newUnit))} ${unitLabel(newUnit, value, t)}`.trim();
  }

  if (isVolume) {
    const inMl = scaled * VOLUME_TO_ML[unit as VolumeUnit];
    const { value, unit: newUnit } = rescaleVolume(inMl, unit as VolumeUnit, unitSystem);
    return `${formatAmount(value, !NON_FRACTIONABLE_UNITS.has(newUnit))} ${unitLabel(newUnit, value, t)}`.trim();
  }

  // Count / Other — scale the number, keep the unit label unchanged
  return [formatAmount(scaled), unitLabel(ingredient.unit, scaled, t)]
    .filter(Boolean)
    .join(" ")
    .trim();
};
